const VARIANT_UPDATE = "variant:update";

/**
 * @typedef {HTMLElement & {
 *  select?: (input: number | { id: string }, event?: Event, options?: { animate?: boolean }) => void
 * }} SlideshowElement
 */

class VariantVideoComponent extends HTMLElement {
  static MOBILE_MEDIA_QUERY = "(max-width: 749px)";

  constructor() {
    super();
    /** @type {SlideshowElement | null} */
    this.slideshow = null;
    /** @type {HTMLButtonElement | null} */
    this.productButton = null;
    /** @type {HTMLButtonElement | null} */
    this.videoButton = null;
    /** @type {MediaQueryList | null} */
    this.mobileMediaQuery = null;
    /** @type {HTMLElement[]} */
    this.slides = [];
    /** @type {HTMLElement[]} */
    this.videoSlides = [];
    /** @type {HTMLElement[]} */
    this.productSlides = [];
    /** @type {HTMLButtonElement[]} */
    this.thumbnails = [];

    this._desiredActiveIndex = -1;
    this._isSyncing = false;
    /** @type {MutationObserver | null} */
    this._thumbnailObserver = null;

    this.handleProductClick = this.handleProductClick.bind(this);
    this.handleVideoClick = this.handleVideoClick.bind(this);
    this.handleSlideshowSelect = this.handleSlideshowSelect.bind(this);
    this.handleVariantUpdate = this.handleVariantUpdate.bind(this);
    this.handleViewportChange = this.handleViewportChange.bind(this);
    this._onThumbnailMutation = this._onThumbnailMutation.bind(this);
  }

  connectedCallback() {
    this.slideshow = /** @type {SlideshowElement | null} */ (this.closest("slideshow-component"));
    if (!this.slideshow) return;

    this.productButton = /** @type {HTMLButtonElement | null} */ (
      this.querySelector(".vp-media-filter-button-product")
    );
    this.videoButton = /** @type {HTMLButtonElement | null} */ (
      this.querySelector(".vp-media-filter-button-video")
    );

    if (!this.productButton || !this.videoButton) return;

    this.mobileMediaQuery = window.matchMedia(VariantVideoComponent.MOBILE_MEDIA_QUERY);
    this.mobileMediaQuery.addEventListener("change", this.handleViewportChange);

    this.productButton.addEventListener("click", this.handleProductClick);
    this.videoButton.addEventListener("click", this.handleVideoClick);
    this.slideshow.addEventListener("slideshow:select", this.handleSlideshowSelect);
    document.addEventListener(VARIANT_UPDATE, this.handleVariantUpdate);

    this._injectFilterStyles();
    this.syncFromDom();
    this.handleViewportChange();
  }

  disconnectedCallback() {
    this._thumbnailObserver?.disconnect();
    this._thumbnailObserver = null;
    this.mobileMediaQuery?.removeEventListener("change", this.handleViewportChange);
    this.productButton?.removeEventListener("click", this.handleProductClick);
    this.videoButton?.removeEventListener("click", this.handleVideoClick);
    this.slideshow?.removeEventListener("slideshow:select", this.handleSlideshowSelect);
    document.removeEventListener(VARIANT_UPDATE, this.handleVariantUpdate);
  }

  _injectFilterStyles() {
    if (document.getElementById("vp-filter-styles")) return;
    const style = document.createElement("style");
    style.id = "vp-filter-styles";
    style.textContent = `
      slideshow-slide.vp-hidden {
        display: none !important;
        visibility: hidden !important;
        pointer-events: none !important;
      }
      .slideshow-controls__thumbnail.vp-hidden {
        display: none !important;
      }
      .vp-media-filter-button-video:disabled,
      .vp-media-filter-button-product:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    `;
    document.head.appendChild(style);
  }

  syncFromDom() {
    if (!this.slideshow) return;

    this.slides = /** @type {HTMLElement[]} */ (
      Array.from(this.slideshow.querySelectorAll("slideshow-slide"))
    );
    this.slides.forEach((slide, index) => {
      if (!slide.getAttribute("slide-id")) {
        slide.setAttribute("slide-id", `vp-slide-${index}`);
      }
    });

    this.videoSlides = this.slides.filter((slide) => this.isVideoSlide(slide));
    this.productSlides = this.slides.filter((slide) => !this.isVideoSlide(slide));

    this.thumbnails = /** @type {HTMLButtonElement[]} */ (
      Array.from(
        this.slideshow.querySelectorAll(
          ".media-gallery__mobile-controls .slideshow-controls__thumbnail"
        )
      )
    );
    if (!this.thumbnails.length) {
      this.thumbnails = /** @type {HTMLButtonElement[]} */ (
        Array.from(this.slideshow.querySelectorAll(".slideshow-controls__thumbnail"))
      );
    }

    this.thumbnails.forEach((thumbnail, index) => {
      thumbnail.dataset.vpOriginalIndex = String(index);
    });

    this._rebindThumbnailObserver();
    this.updateVideoButtonAvailability();
  }

  _rebindThumbnailObserver() {
    this._thumbnailObserver?.disconnect();
    this._thumbnailObserver = null;
    if (!this.thumbnails.length) return;

    this._thumbnailObserver = new MutationObserver(this._onThumbnailMutation);
    this.thumbnails.forEach((thumbnail) => {
      this._thumbnailObserver?.observe(thumbnail, {
        attributes: true,
        attributeFilter: ["aria-selected"],
      });
    });
  }

  _onThumbnailMutation() {
    if (this._desiredActiveIndex < 0) return;

    this._thumbnailObserver?.disconnect();

    this.thumbnails.forEach((thumbnail, index) => {
      thumbnail.setAttribute("aria-selected", String(index === this._desiredActiveIndex));
    });

    this.thumbnails.forEach((thumbnail) => {
      this._thumbnailObserver?.observe(thumbnail, {
        attributes: true,
        attributeFilter: ["aria-selected"],
      });
    });
  }

  handleViewportChange() {
    const isMobile = this.mobileMediaQuery?.matches;
    this.hidden = !isMobile;

    if (!isMobile) {
      this._desiredActiveIndex = -1;
      this.showAllSlides();
      return;
    }

    this.applyFilter("product");
  }

  handleVariantUpdate() {
    this.syncFromDom();
    if (!this.mobileMediaQuery?.matches) return;
    this.applyFilter("product");
  }

  handleProductClick() {
    this.applyFilter("product");
  }

  handleVideoClick() {
    if (!this.videoSlides.length) return;
    this.applyFilter("video");
  }

  /**
   * @param {CustomEvent<{ slide?: HTMLElement }>} event
   */
  handleSlideshowSelect(event) {
    if (!this.mobileMediaQuery?.matches) return;
    if (!event?.detail?.slide) return;
    if (this._isSyncing) return;

    const selectedType = this.isVideoSlide(event.detail.slide) ? "video" : "product";
    this.setActiveButton(selectedType);

    const activeIndex = this.slides.indexOf(event.detail.slide);
    if (activeIndex >= 0) {
      this._setDesiredActiveIndex(activeIndex);
    }
  }

  /**
   * @param {"product" | "video"} filterType
   */
  applyFilter(filterType) {
    if (!this.mobileMediaQuery?.matches) return;

    const showVideo = filterType === "video";
    const visibleSlides = showVideo ? this.videoSlides : this.productSlides;
    if (!visibleSlides.length) return;

    this.slides.forEach((slide) => {
      const shouldShow = showVideo ? this.isVideoSlide(slide) : !this.isVideoSlide(slide);
      slide.classList.toggle("vp-hidden", !shouldShow);
    });

    this.syncThumbnailVisibility(showVideo);
    this.setActiveButton(filterType);

    const targetSlide = visibleSlides[0];
    if (!targetSlide) return;

    const domIndex = this.slides.indexOf(targetSlide);
    if (domIndex < 0) return;

    this._setDesiredActiveIndex(domIndex);

    this._isSyncing = true;
    this._navigateToSlide(domIndex);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this._isSyncing = false;
      });
    });
  }

  showAllSlides() {
    this.slides.forEach((slide) => slide.classList.remove("vp-hidden"));
    this.thumbnails.forEach((thumbnail) => thumbnail.classList.remove("vp-hidden"));
    this.setActiveButton("product");
  }

  /**
   * @param {number} domIndex
   */
  _navigateToSlide(domIndex) {
    if (!this.slideshow || typeof this.slideshow.select !== "function") {
      this.slides[domIndex]?.scrollIntoView({
        behavior: "instant",
        inline: "start",
        block: "nearest",
      });
      return;
    }

    try {
      this.slideshow.select(domIndex, undefined, { animate: false });
    } catch (_) {
      this.slides[domIndex]?.scrollIntoView({
        behavior: "instant",
        inline: "start",
        block: "nearest",
      });
    }
  }

  /**
   * @param {number} index
   */
  _setDesiredActiveIndex(index) {
    this._desiredActiveIndex = index;

    this._thumbnailObserver?.disconnect();

    this.thumbnails.forEach((thumbnail, i) => {
      thumbnail.setAttribute("aria-selected", String(i === index));
    });

    const activeThumbnail = this.thumbnails[index];
    if (activeThumbnail && !activeThumbnail.classList.contains("vp-hidden")) {
      activeThumbnail.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }

    this.thumbnails.forEach((thumbnail) => {
      this._thumbnailObserver?.observe(thumbnail, {
        attributes: true,
        attributeFilter: ["aria-selected"],
      });
    });
  }

  /**
   * @param {"product" | "video"} type
   */
  setActiveButton(type) {
    const isProduct = type === "product";
    const isVideo = type === "video";

    this.productButton?.classList.toggle("is-active", isProduct);
    this.videoButton?.classList.toggle("is-active", isVideo);

    if (this.productButton) this.productButton.setAttribute("aria-pressed", String(isProduct));
    if (this.videoButton) this.videoButton.setAttribute("aria-pressed", String(isVideo));

    this.updateVideoButtonAvailability();
  }

  updateVideoButtonAvailability() {
    if (!this.videoButton) return;
    const shouldDisable = this.videoSlides.length === 0;
    this.videoButton.disabled = shouldDisable;
    this.videoButton.setAttribute("aria-disabled", String(shouldDisable));
  }

  /**
   * @param {boolean} showVideo
   */
  syncThumbnailVisibility(showVideo) {
    if (!this.thumbnails.length) return;

    this.thumbnails.forEach((thumbnail, index) => {
      const originalIndex = Number(thumbnail.dataset.vpOriginalIndex ?? index);
      const slide = this.slides[originalIndex];
      if (!slide) return;
      const shouldShow = showVideo ? this.isVideoSlide(slide) : !this.isVideoSlide(slide);
      thumbnail.classList.toggle("vp-hidden", !shouldShow);
    });
  }

  /**
   * @param {HTMLElement} slide
   */
  isVideoSlide(slide) {
    if (slide.matches(".product-media-container--video, .product-media-container--external_video")) {
      return true;
    }
    return Boolean(
      slide.querySelector(".product-media-container--video, .product-media-container--external_video")
    );
  }
}

if (!customElements.get("variant-video-component")) {
  customElements.define("variant-video-component", VariantVideoComponent);
}